import React from 'react';
import { Table, Tooltip, Typography, Empty, Space, Alert } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import PdfExportStatusChip from './PdfExportStatusChip';
import { PdfExport } from './types';

interface Props {
    rows: PdfExport[];
    loading: boolean;
    error: string | null;
}

const formatDateTime = (iso: string | null): string => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
};

const formatDuration = (durationMs: number | null, queuedAt: string | null, finishedAt: string | null): string => {
    if (typeof durationMs === 'number') return formatMs(durationMs);
    if (queuedAt && finishedAt) return formatMs(new Date(finishedAt).getTime() - new Date(queuedAt).getTime());
    return '—';
};

const formatMs = (ms: number): string => {
    if (ms < 0) return '—';
    if (ms < 1000) return `${Math.round(ms)} ms`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
    const minutes = Math.floor(ms / 60_000);
    const seconds = Math.round((ms % 60_000) / 1000);
    return `${minutes}m ${seconds}s`;
};

const formatBytes = (bytes: number | null): string => {
    if (typeof bytes !== 'number') return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const truncated = (value: string | null, max: number = 12) => {
    if (!value) return '—';
    if (value.length <= max) return value;
    return value.slice(0, max) + '…';
};

const PdfExportsTable: React.FC<Props> = ({ rows, loading, error }) => {
    const columns: ColumnsType<PdfExport> = [
        {
            title: 'Status',
            key: 'status',
            width: 150,
            render: (_, row) => <PdfExportStatusChip status={row.status} />,
        },
        {
            title: 'Account',
            key: 'accountId',
            width: 140,
            render: (_, row) => (
                <Tooltip title={row.accountId ?? '—'}>
                    <Typography.Text copyable={{ text: row.accountId ?? '' }}>
                        {truncated(row.accountId)}
                    </Typography.Text>
                </Tooltip>
            ),
        },
        {
            title: 'Document / Topic',
            key: 'documentId',
            width: 180,
            render: (_, row) => {
                if (row.documentId) {
                    return (
                        <Tooltip title={row.documentId}>
                            <Typography.Text copyable={{ text: row.documentId }}>
                                {truncated(row.documentId)}
                            </Typography.Text>
                        </Tooltip>
                    );
                }
                if (row.topicVersionId) {
                    return (
                        <Tooltip title={`Docless topic version ${row.topicVersionId}`}>
                            <Typography.Text copyable={{ text: row.topicVersionId }}>
                                <Typography.Text type="secondary">topic </Typography.Text>
                                {truncated(row.topicVersionId)}
                            </Typography.Text>
                        </Tooltip>
                    );
                }
                return '—';
            },
        },
        {
            title: 'Lang',
            dataIndex: 'languageCode',
            key: 'languageCode',
            width: 80,
        },
        {
            title: 'Queued',
            key: 'queuedAt',
            width: 200,
            render: (_, row) => formatDateTime(row.queuedAt),
        },
        {
            title: 'Duration',
            key: 'duration',
            width: 110,
            render: (_, row) => formatDuration(row.durationMs, row.queuedAt, row.finishedAt),
        },
        {
            title: 'Size',
            key: 'pdfSizeBytes',
            width: 100,
            render: (_, row) => formatBytes(row.pdfSizeBytes),
        },
        {
            title: 'Attempts',
            dataIndex: 'attemptCount',
            key: 'attemptCount',
            width: 90,
        },
        {
            title: 'Trigger',
            dataIndex: 'trigger',
            key: 'trigger',
            width: 130,
        },
        {
            title: 'Triggered by',
            key: 'triggeredBy',
            width: 160,
            render: (_, row) => row.triggeredBy ?? '—',
        },
    ];

    if (error) {
        return (
            <Alert type="error" showIcon message="Couldn't load PDF exports" description={error} />
        );
    }

    return (
        <Table<PdfExport>
            rowKey="id"
            size="small"
            columns={columns}
            dataSource={rows}
            loading={loading}
            pagination={false}
            scroll={{ x: 1400 }}
            expandable={{
                expandedRowRender: (row) => (
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <div>
                            <Typography.Text strong>Publish ID: </Typography.Text>
                            <Typography.Text copyable>{row.publishId}</Typography.Text>
                        </div>
                        {row.documentVersionId && (
                            <div>
                                <Typography.Text strong>Document version: </Typography.Text>
                                <Typography.Text copyable>{row.documentVersionId}</Typography.Text>
                            </div>
                        )}
                        {row.topicVersionId && (
                            <div>
                                <Typography.Text strong>Topic version: </Typography.Text>
                                <Typography.Text copyable>{row.topicVersionId}</Typography.Text>
                            </div>
                        )}
                        {row.pdfGcsPath && (
                            <div>
                                <Typography.Text strong>GCS path: </Typography.Text>
                                <Typography.Text copyable code>{row.pdfGcsPath}</Typography.Text>
                            </div>
                        )}
                        {row.errorCode && (
                            <div>
                                <Typography.Text strong>Error code: </Typography.Text>
                                <Typography.Text type="danger">{row.errorCode}</Typography.Text>
                            </div>
                        )}
                        {row.errorMessage && (
                            <div>
                                <Typography.Text strong>Error: </Typography.Text>
                                <Typography.Text type="danger" style={{ whiteSpace: 'pre-wrap' }}>
                                    {row.errorMessage}
                                </Typography.Text>
                            </div>
                        )}
                    </Space>
                ),
            }}
            locale={{
                emptyText: (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No PDF exports match these filters."
                    />
                ),
            }}
        />
    );
};

export default PdfExportsTable;
